import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { pageSEO } from '../utils/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuthStore } from '../stores/authStore';
import { getKakaoLoginUrl } from '../services/kakaoAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const ReviewFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated } = useAuthStore();

  const [rating, setRating] = useState<number>(5);
  const [content, setContent] = useState('');
  const [hoveredRating, setHoveredRating] = useState<number>(0);

  // 별 클릭 핸들러 (왼쪽/오른쪽 구분)
  const handleStarClick = (starIndex: number, isLeftHalf: boolean) => {
    const newRating = isLeftHalf ? starIndex - 0.5 : starIndex;
    setRating(newRating);
  };

  // 별 호버 핸들러
  const handleStarHover = (starIndex: number, isLeftHalf: boolean) => {
    const newRating = isLeftHalf ? starIndex - 0.5 : starIndex;
    setHoveredRating(newRating);
  };

  // 별의 채워진 정도 계산 (0: 빈 별, 0.5: 반쪽, 1: 가득)
  const getStarFillLevel = (starIndex: number, currentRating: number): number => {
    if (currentRating >= starIndex) return 1;
    if (currentRating >= starIndex - 0.5) return 0.5;
    return 0;
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [reservation, setReservation] = useState<any>(null);
  const seo = pageSEO.review;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!token) {
      alert('잘못된 접근입니다.');
      navigate('/');
      return;
    }

    // 토큰으로 예약 정보 조회 (인증 불필요)
    const fetchReservation = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/reviews/token/${token}`);
        setReservation(response.data);

        // 이미 리뷰가 작성된 경우
        if (response.data.has_review) {
          setRating(response.data.review.rating);
          setContent(response.data.review.content);
        }
      } catch (err: any) {
        console.error('Failed to fetch reservation:', err);
        alert(err.response?.data?.detail || '예약 정보를 불러올 수 없습니다.');
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReservation();
  }, [token, navigate]);

  const handleKakaoLogin = () => {
    // 현재 페이지 URL을 저장하여 로그인 후 돌아오기
    const currentUrl = window.location.pathname + window.location.search;
    localStorage.setItem('redirect_after_login', currentUrl);

    // 카카오 로그인 URL 생성
    const kakaoAuthUrl = getKakaoLoginUrl({
      clientId: process.env.REACT_APP_KAKAO_CLIENT_ID || '',
      redirectUri: `${window.location.origin}/kakao/callback`,
    });

    window.location.href = kakaoAuthUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (content.length < 10) {
      setError('리뷰 내용은 최소 10자 이상 입력해주세요.');
      return;
    }

    if (reservation?.has_review) {
      alert('이미 리뷰가 작성된 예약입니다.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API_BASE_URL}/reviews/token/${token}`, {
        rating,
        content,
      });

      if (isAuthenticated) {
        alert('리뷰가 성공적으로 등록되었습니다! 리뷰 작성 혜택이 지급되었습니다.');
      } else {
        alert('리뷰가 성공적으로 등록되었습니다!');
      }
      navigate('/my-reservations');
    } catch (err: any) {
      console.error('Review submission error:', err);
      setError(err.response?.data?.detail || '리뷰 등록에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://goodtimerent.kr/review" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <Header />

        {/* Content */}
        {isLoading ? (
          <div className="max-w-3xl mx-auto px-4 py-12">
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">예약 정보를 불러오는 중...</p>
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-4 py-12">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {reservation?.has_review ? '작성하신 리뷰' : '이용하신 차량은 어떠셨나요?'}
                </h2>
                <p className="text-gray-600">{reservation?.vehicle_name}</p>
                <p className="text-sm text-gray-500 mt-1">예약번호: {reservation?.reservation_number}</p>
              </div>

              {/* 예약 정보 카드 */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">예약 정보</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500 mb-1">차량종류</p>
                    <p className="font-semibold text-gray-900">{reservation?.vehicle_class}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">이용시간</p>
                    <p className="font-semibold text-gray-900">
                      {reservation?.start_date && reservation?.end_date && (
                        (() => {
                          const start = new Date(reservation.start_date);
                          const end = new Date(reservation.end_date);
                          const diffMs = end.getTime() - start.getTime();
                          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
                          const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

                          if (diffDays > 0) {
                            return diffHours > 0 ? `${diffDays}일 ${diffHours}시간` : `${diffDays}일`;
                          } else {
                            return `${diffHours}시간`;
                          }
                        })()
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">대여일</p>
                    <p className="font-semibold text-gray-900">
                      {reservation?.start_date && (() => {
                        const date = new Date(reservation.start_date);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}.${month}.${day} ${hours}:${minutes}`;
                      })()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500 mb-1">반납일</p>
                    <p className="font-semibold text-gray-900">
                      {reservation?.end_date && (() => {
                        const date = new Date(reservation.end_date);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}.${month}.${day} ${hours}:${minutes}`;
                      })()}
                    </p>
                  </div>
                </div>
              </div>

              {/* 비로그인 사용자 안내 */}
              {!isAuthenticated && !reservation?.has_review && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-blue-900 mb-2">
                        리뷰 작성 혜택을 받으시려면 로그인이 필요합니다
                      </h4>
                      <p className="text-sm text-blue-800 mb-4">
                        카카오 3초 간편가입으로 리뷰 작성 시 쿠폰을 받으실 수 있습니다.
                        비회원으로도 리뷰 작성은 가능하지만 혜택은 제공되지 않습니다.
                      </p>
                      <button
                        onClick={handleKakaoLogin}
                        className="w-full bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 01-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3z"/>
                        </svg>
                        카카오 3초 간편가입하고 혜택받기
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {reservation?.has_review ? (
                /* 이미 작성된 리뷰 표시 */
                <div className="space-y-6">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-4">별점</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((starIndex) => {
                        const fillLevel = getStarFillLevel(starIndex, rating);
                        return (
                          <div key={starIndex} className="relative w-12 h-12">
                            <svg
                              className="absolute top-0 left-0 w-12 h-12"
                              viewBox="0 0 24 24"
                            >
                              <defs>
                                <linearGradient id={`star-display-gradient-${starIndex}`}>
                                  <stop
                                    offset={fillLevel === 0.5 ? '50%' : '0%'}
                                    stopColor={fillLevel >= 0.5 ? '#FBBF24' : '#D1D5DB'}
                                  />
                                  <stop
                                    offset={fillLevel === 0.5 ? '50%' : '0%'}
                                    stopColor={fillLevel === 1 ? '#FBBF24' : '#D1D5DB'}
                                  />
                                </linearGradient>
                              </defs>
                              <path
                                fill={`url(#star-display-gradient-${starIndex})`}
                                stroke={fillLevel > 0 ? '#FBBF24' : '#D1D5DB'}
                                strokeWidth={1}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                              />
                            </svg>
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{rating}점</p>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      이용 후기
                    </label>
                    <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50">
                      {content}
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800">이미 리뷰가 작성되었습니다. 소중한 후기 감사합니다!</p>
                  </div>

                  <button
                    type="button"
                    onClick={() => navigate('/my-reservations')}
                    className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                  >
                    내 예약으로 돌아가기
                  </button>
                </div>
              ) : (
                /* 리뷰 작성 폼 */
                <form onSubmit={handleSubmit}>
                  {/* 별점 */}
                  <div className="mb-8">
                    <label className="block text-lg font-medium text-gray-700 mb-4">별점</label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((starIndex) => {
                        const fillLevel = getStarFillLevel(starIndex, hoveredRating || rating);
                        return (
                          <div
                            key={starIndex}
                            className="relative w-12 h-12 cursor-pointer transition-transform hover:scale-110"
                            onMouseLeave={() => setHoveredRating(0)}
                          >
                            {/* 왼쪽 절반 클릭 영역 */}
                            <div
                              className="absolute left-0 top-0 w-6 h-12 z-10"
                              onClick={() => handleStarClick(starIndex, true)}
                              onMouseEnter={() => handleStarHover(starIndex, true)}
                            />
                            {/* 오른쪽 절반 클릭 영역 */}
                            <div
                              className="absolute right-0 top-0 w-6 h-12 z-10"
                              onClick={() => handleStarClick(starIndex, false)}
                              onMouseEnter={() => handleStarHover(starIndex, false)}
                            />

                            {/* 별 SVG */}
                            <svg
                              className="absolute top-0 left-0 w-12 h-12 pointer-events-none"
                              viewBox="0 0 24 24"
                            >
                              <defs>
                                <linearGradient id={`star-gradient-${starIndex}`}>
                                  <stop
                                    offset={fillLevel === 0.5 ? '50%' : '0%'}
                                    stopColor={fillLevel >= 0.5 ? '#FBBF24' : '#D1D5DB'}
                                  />
                                  <stop
                                    offset={fillLevel === 0.5 ? '50%' : '0%'}
                                    stopColor={fillLevel === 1 ? '#FBBF24' : '#D1D5DB'}
                                  />
                                </linearGradient>
                              </defs>
                              <path
                                fill={`url(#star-gradient-${starIndex})`}
                                stroke={fillLevel > 0 ? '#FBBF24' : '#D1D5DB'}
                                strokeWidth={1}
                                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                              />
                            </svg>
                          </div>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{rating}점</p>
                  </div>

                  {/* 리뷰 내용 */}
                  <div className="mb-6">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      이용 후기
                    </label>
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      rows={6}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      placeholder="차량 이용 경험을 자유롭게 작성해주세요. (최소 10자)"
                      required
                    />
                    <p className="mt-2 text-sm text-gray-600">{content.length}자</p>
                  </div>

                  {error && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                      {error}
                    </div>
                  )}

                  {/* 버튼 */}
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => navigate('/my-reservations')}
                      className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors"
                    >
                      나중에 작성하기
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting || content.length < 10}
                      className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? '등록 중...' : '리뷰 등록'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

        <Footer />
      </div>
    </>
  );
};

export default ReviewFormPage;
